import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Sparkles, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { ACCENT, planPrice, BillingToggle, PlanComparison } from "./plans";

export default function SubscriptionPlans({ open, onClose, current, onChanged }) {
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (open) api.get("/subscription/plans").then(({ data }) => setPlans(data));
  }, [open]);

  const choose = async (plan) => {
    setBusy(plan.id);
    try {
      const { data } = await api.post("/subscription/checkout", { plan: plan.id, cycle, origin_url: window.location.origin });
      if (data.demo) {
        toast.success("Demo tier activated 🎬");
        onChanged && onChanged();
        onClose();
      } else if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="plans-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <span className="text-[#FF5A79]">Bubz +</span> Plans
          </DialogTitle>
        </DialogHeader>
        <p className="text-slate-500 -mt-2">Pick a streaming plan. Paid plans start with a 7-day free trial. Cancel anytime.</p>
        <div className="flex justify-center mt-2"><BillingToggle cycle={cycle} setCycle={setCycle} /></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          {plans.map((p) => {
            const active = current === p.id;
            const accent = ACCENT[p.id] || "#FF5A79";
            const pr = planPrice(p, cycle);
            const paid = p.price > 0;
            return (
              <div key={p.id} data-testid={`plan-${p.id}`}
                className={`rounded-3xl border-2 p-5 flex flex-col ${active ? "" : "border-slate-200"}`}
                style={active ? { borderColor: accent } : {}}>
                <div className="flex items-center gap-2 flex-wrap mb-1 min-h-[22px]">
                  {p.id === "standard" && <span className="rounded-full bg-[#FF5A79] text-white px-3 py-0.5 text-xs font-bold">Popular</span>}
                  {paid && <span className="rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[11px] font-bold">7-day trial</span>}
                </div>
                <div className="font-display text-xl font-bold text-slate-900">{p.name}</div>
                <div className="mt-1">
                  {pr.label === "Free" ? <span className="text-2xl font-bold" style={{ color: accent }}>Free</span>
                    : <><span className="text-3xl font-display font-bold text-slate-900">{pr.label}</span><span className="text-slate-400 text-sm">{pr.suffix}</span></>}
                </div>
                {pr.note && <div className="text-[11px] font-semibold text-emerald-600 mt-0.5">{pr.note}</div>}
                <div className="text-xs font-semibold text-slate-400 mt-1">{p.quality} · {p.devices}</div>
                <ul className="mt-4 space-y-2 flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: accent }} /> {f}
                    </li>
                  ))}
                </ul>
                <Button data-testid={`subscribe-${p.id}`} disabled={active || busy === p.id} onClick={() => choose(p)}
                  className="mt-5 w-full h-11 rounded-xl font-bold text-white"
                  style={{ backgroundColor: active ? "#cbd5e1" : accent }}>
                  {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : active ? "Current plan" : p.id === "demo" ? "Use Demo" : "Start free trial"}
                </Button>
              </div>
            );
          })}
        </div>
        <div className="mt-5"><PlanComparison plans={plans} cycle={cycle} /></div>
        <p className="text-xs text-slate-400 mt-3 flex items-center gap-1"><Sparkles className="h-3 w-3" /> Paid plans use Stripe test mode — card 4242 4242 4242 4242.</p>
      </DialogContent>
    </Dialog>
  );
}
